import type { Validation } from '@m/types/api';

/**
 * Body for `PATCH /admin/location/:id/ops-info`.
 *
 * Fields are sparse with PATCH semantics:
 *  - key omitted        -> leave value unchanged
 *  - key with `null`    -> clear the value
 *  - key with a value   -> set the value
 */
export type SiteOpsInfoForm = {
  notes?: string | null;
};

/**
 * Response shape for `GET /admin/location/:id/ops-info` and the `PATCH`
 * counterpart.
 */
export type SiteOpsInfoView = {
  notes: string | null;
};

export enum ZoneAmenity {
  EV_CHARGER = 'AMENITY_EV_CHARGER',
  WHEELCHAIR_ACCESSIBLE = 'AMENITY_WHEELCHAIR_ACCESSIBLE',
  PUBLIC_RESTROOM = 'AMENITY_PUBLIC_RESTROOM',
  IN_OUT_ACCESS = 'AMENITY_IN_OUT_ACCESS',
  ON_SITE_STAFF = 'AMENITY_ON_SITE_STAFF',
  BATTERY_JUMP = 'AMENITY_BATTERY_JUMP',
  TIRE_INFLATION = 'AMENITY_TIRE_INFLATION',
}

export const ZONE_AMENITY_LABELS: Record<ZoneAmenity, string> = {
  [ZoneAmenity.EV_CHARGER]: 'EV Charger',
  [ZoneAmenity.WHEELCHAIR_ACCESSIBLE]: 'Wheelchair Accessible',
  [ZoneAmenity.PUBLIC_RESTROOM]: 'Public Restroom',
  [ZoneAmenity.IN_OUT_ACCESS]: 'In and Out Access',
  [ZoneAmenity.ON_SITE_STAFF]: 'On-site Staff',
  [ZoneAmenity.BATTERY_JUMP]: 'Battery Jump',
  [ZoneAmenity.TIRE_INFLATION]: 'Tire Inflation',
};

export type Location = {
  allHoursOfOperation: OperationHours[];
  amenities?: ZoneAmenity[];
  assetProfile?: string; // ENUM
  capacity?: number;
  city: string;
  contractType?: string;
  hoursOfOperation: {
    today: BaseOperationHours;
  };
  id: number;
  internalName?: string;
  internetAvailability?: string;
  latitude: number;
  locationType?: string; // ENUM
  longitude: number;
  name: string;
  operatorId: number;
  operatorSiteId: number;
  pointOfContact?: ZonePointOfContact;
  sitePlatformSettings: SitePlatformSettings;
  squareFeet?: number;
  squareLocationId: string;
  state: string; // ENUM
  status: string; // ENUM
  street: string;
  taxPercent?: number | null;
  timezone?: string;
  uuid: string;
  zip: string;
  zoneId?: string;
};

/**
 * Operator-facing contact person for a zone. Mirrors the legacy site-api
 * `pointOfContact` shape (all fields optional; the backend treats empty
 * strings as missing).
 */
export type ZonePointOfContact = {
  email?: string;
  firstName?: string;
  lastName?: string;
};

export interface OperationHours extends BaseOperationHours {
  dayOfWeek: string;
}

interface BaseOperationHours {
  close: string;
  open: string;
}

export type UpdateHoursPayload = {
  friday?: OperationHours;
  monday?: OperationHours;
  saturday?: OperationHours;
  sunday?: OperationHours;
  thursday?: OperationHours;
  tuesday?: OperationHours;
  wednesday?: OperationHours;
};

export type EffectiveStartDateOptionView = {
  dateDisplay: string;
  dateValue: string | null;
};

export type SiteEffectiveSubscriptionStartDateView = {
  currentEffectiveStartDate: EffectiveStartDateOptionView;
  currentOptionIndex: number;
  effectiveStartDateOptions: EffectiveStartDateOptionView[];
  isEditable: boolean;
};

export type SubscriptionConcurrentSessionsLimitPolicy = {
  admissionPolicy?: 'Admit' | 'Deny';
};

export type SitePlatformSettings = {
  autoChargeVisitConfig: {
    laneConfigSummary?: {
      autoChargeOnExitLanes: number;
      disabledLanes: number;
      totalLanes: number;
    };
  };
  autoCloseVisitConfig: {
    autoCloseDelay: number;
    enabled: boolean;
  };
  consumerVisible: boolean;
  consumerVisibleOverride: boolean;
  disableGuestRequests?: boolean;
  disableTextToPayFallbackVendEnabled?: boolean;
  hospitalityStatusEnabled: boolean;
  missingRatesEnabled?: boolean;
  noTechEnabled: boolean;
  seamlessExitEnabled: boolean;
  selfParkEnabled: boolean;
  siteEffectiveSubscriptionStartDateView: SiteEffectiveSubscriptionStartDateView;
  status: LocationSettingStatusEnum;
  subscriptionVisible: boolean;
  subscriptionVisibleOverride: boolean;
  tippingEnabled: boolean;
  uberEnabled: boolean;
  uberVisible: boolean;
  valetEnabled: boolean;
  valetOffsiteEnabled?: boolean;
  valetOnsiteEnabled?: boolean;
};

export type LocationValidation = {
  active: boolean;
  addedAt: number;
  id: number;
  updatedAt: number;
  vendorName: string;
};

export type LocationValidationV2 = {
  enterpriseId: number | null;
  enterpriseLocationId: number | null;
  validation: {
    active: boolean;
    addedAt: number;
    id: number;
    name: string | null;
    updatedAt: number;
    validationPlan: Validation;
    vendorName: string;
  };
};

export type LocationGateActions = {
  alwaysAutoVend: boolean;
  memberAccess: boolean;
  subscriberAccess: boolean;
  textToPay: boolean;
  textToVend: boolean;
};

export type LocationLane = {
  autoChargeVisitConfig: AutoChargeVisitConfig;
  cameraEnabled: boolean;
  direction: string;
  gateActive: boolean;
  hasCamera: boolean;
  hasGate: boolean;
  id: number;
  name: string;
  siteId: number;
  staffingConfig?: StaffingConfig;
  textShortCode: string | null;
};

export enum StaffingConfig {
  STAFFED = 'Staffed',
  UNSTAFFED = 'Unstaffed',
}

export enum AutoChargeVisitConfig {
  DISABLED = 'Disabled',
  ON_EXIT = 'OnExit',
}

export type CustomerInitiatedAccessStatus = 'Unspecified' | 'Enabled' | 'Disabled';

export type CustomerInitiatedAccess = {
  geofenceRadiusMeters?: number;
  status: CustomerInitiatedAccessStatus;
};

/** Operator-facing response for the CIA gate-access QR endpoint. */
export type GateAccessQrView = {
  gateAccessUrl: string;
  qrCodeImageUrl: string;
  uuid: string;
};

export type GateActions = {
  automatic: boolean;
  customerInitiatedAccess?: CustomerInitiatedAccess;
  nestedZone: boolean;
  parkingPass: boolean;
  reservation: boolean;
  signCodeCode: string;
  subscriber: boolean;
  textToPay: boolean;
  textToVend: boolean;
  valetExit: boolean;
  vendOnSuccessfulCharge: boolean;
};

export type GateStatus = {
  active: boolean;
  enabled: boolean;
  gateToken: string;
  gateType: {
    id: number;
    name: string;
  };
  id: number;
  laneId: number;
};

export type ControlDefaults = {
  pulseDelayMs: number;
  pulseLengthMs: number;
};

export type LocationLaneDetail = {
  edgeDevice?: {
    controlDefaults: ControlDefaults;
    deviceIp: string;
    deviceName: string;
    deviceUuid: string;
    gateNotes: string;
    gateUuid: string;
    relayNumber: number;
  };
  equipmentDetails?: {
    approachSettingType: string;
    cameraIp: string;
    computeIp: string;
    controlDefaults: ControlDefaults;
    deviceIp: string;
    deviceName: string;
    deviceUuid: string;
    enabled: boolean;
    gateNotes: string;
    gateUuid: string;
    id: number;
    linkedSiteId?: number;
    make: string;
    notes?: string;
    serial: string;
  } | null;
  gateActions?: GateActions;
  gateDetails?: {
    active: boolean;
    directedLaneId: number;
    gateToken: string;
    id: number;
    laneId: number;
  } | null;
  gateStatus?: GateStatus;
  laneDetails: {
    autoChargeVisitConfig?: AutoChargeVisitConfig;
    directedLaneId: number;
    direction: string;
    id: number;
    name: string;
    siteId: number;
    staffingConfig?: StaffingConfig;
  };
  signCode?: string | null;
};

export enum LocationSettingStatusEnum {
  ONBOARDING = 'Onboarding',
  INACTIVE = 'Inactive',
  ACTIVE = 'Active',
}

export enum LaneDirectionEnum {
  ENTRY = 'Entry',
  EXIT = 'Exit',
}

export const isExitLane = (laneDirection?: LaneDirectionEnum) =>
  laneDirection === LaneDirectionEnum.EXIT;
